import { useState, useCallback, useMemo } from 'react';
import { TextBox } from 'devextreme-react/text-box';
import { SelectBox } from 'devextreme-react/select-box';
import { CheckBox } from 'devextreme-react/check-box';
import { NumberBox } from 'devextreme-react/number-box';
import { TagBox } from 'devextreme-react/tag-box';
import { Button } from 'devextreme-react/button';
import { useDesignerStore } from '../store';
import { MOCK_ACCOUNTS } from '../mock-data';
import { AccountFilterDialog } from '../dialogs/AccountFilterDialog';
import { FormulaEditor } from './FormulaEditor';
import {
  flattenTree,
  collectRowIds,
  validateRowId,
  getColumnHeadersNodes,
  resolveColumnHeadersId,
} from '@calculom/report-designer';
import type { RowNode } from '@calculom/report-designer';
import type {
  RowDefinition,
  RowStyle,
  AccountType,
  LineBorderStyle,
  TitleBlockAlignment,
  HeaderRow,
  SectionRow,
  AccountRow,
  SubtotalRow,
  CalculatedRow,
  GrandTotalRow,
  LineRow,
  TitleBlockRow,
  ColumnHeadersRow,
} from '@calculom/report-designer';

// ============================================================
// Shared form controls
// ============================================================

const STYLE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Bold' },
  { value: 'underline', label: 'Underline' },
  { value: 'double_underline', label: 'Double Underline' },
];

const ACCOUNT_TYPES: { value: AccountType; label: string }[] = [
  { value: 'asset', label: 'Asset' },
  { value: 'liability', label: 'Liability' },
  { value: 'equity', label: 'Equity' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'expense', label: 'Expense' },
];

const HEADING_LEVELS = [
  { value: 1, label: 'H1' },
  { value: 2, label: 'H2' },
  { value: 3, label: 'H3' },
];

const TITLE_BLOCK_ALIGNMENTS: { value: TitleBlockAlignment; label: string }[] = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const LINE_BORDER_STYLES: { value: LineBorderStyle; label: string }[] = [
  { value: 'thin', label: 'Thin' },
  { value: 'medium', label: 'Medium' },
  { value: 'thick', label: 'Thick' },
  { value: 'double', label: 'Double' },
];

const CHILD_ACCOUNTS = MOCK_ACCOUNTS.filter((a) => a.parent !== null).map((a) => ({
  code: a.code,
  display: `${a.code} - ${a.name}`,
}));

function TextProperty({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <TextBox
        value={value}
        onValueChanged={(e) => onChange(e.value ?? '')}
        placeholder={placeholder}
        valueChangeEvent="blur"
      />
    </div>
  );
}

function StyleSelector({
  value,
  onChange,
}: {
  value: RowStyle | undefined;
  onChange: (v: RowStyle) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Style</label>
      <SelectBox
        items={STYLE_OPTIONS}
        displayExpr="label"
        valueExpr="value"
        value={value ?? 'normal'}
        onValueChanged={(e) => onChange(e.value as RowStyle)}
      />
    </div>
  );
}

// ============================================================
// Row ID Editor
// ============================================================

function RowIdField({ node }: { node: RowNode }) {
  const { nodes, updateNodeRowId } = useDesignerStore();
  const [localValue, setLocalValue] = useState(node.rowId);
  const [error, setError] = useState<string | null>(null);

  const handleBlur = useCallback(() => {
    if (localValue === node.rowId) {
      setError(null);
      return;
    }
    const allRowIds = collectRowIds(nodes);
    const err = validateRowId(localValue, allRowIds, node.rowId);
    if (err) {
      setError(err);
    } else {
      setError(null);
      updateNodeRowId(node.id, localValue);
    }
  }, [localValue, node.rowId, node.id, nodes, updateNodeRowId]);

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Row ID</label>
      <TextBox
        value={localValue}
        onValueChanged={(e) => setLocalValue(e.value ?? '')}
        valueChangeEvent="input"
        onFocusOut={handleBlur}
        isValid={!error}
      />
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}

// ============================================================
// Column Headers Selector (shared by all data row types)
// ============================================================

const DATA_ROW_TYPES = new Set(['account', 'section', 'subtotal', 'calculated', 'grand_total']);

function ColumnHeadersSelector({ node }: { node: RowNode }) {
  const { nodes, updateNodeColumnHeadersId } = useDesignerStore();

  if (!DATA_ROW_TYPES.has(node.data.type)) return null;

  const headerNodes = getColumnHeadersNodes(nodes);

  if (headerNodes.length === 0) {
    return (
      <div className="text-xs text-gray-400 bg-gray-50 rounded p-2">
        No column headers on canvas — using default columns
      </div>
    );
  }

  if (headerNodes.length === 1) {
    const headerLabel = (headerNodes[0]!.data as ColumnHeadersRow).label || headerNodes[0]!.rowId;
    return (
      <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
        Using column set: <span className="font-medium text-gray-700">{headerLabel}</span>
      </div>
    );
  }

  // 2+ column_headers: show dropdown
  const resolvedId = resolveColumnHeadersId(node, nodes);
  const items = headerNodes.map((h) => ({
    id: h.id,
    label: (h.data as ColumnHeadersRow).label || h.rowId,
  }));

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Column Set</label>
      <SelectBox
        items={items}
        displayExpr="label"
        valueExpr="id"
        value={resolvedId ?? null}
        onValueChanged={(e) => {
          if (e.value) {
            updateNodeColumnHeadersId(node.id, e.value as string);
          }
        }}
        placeholder="Select column set..."
      />
    </div>
  );
}

// ============================================================
// Per-row-type property editors
// ============================================================

function HeaderProperties({
  row,
  onUpdate,
}: {
  row: HeaderRow;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  return (
    <>
      <TextProperty
        label="Text"
        value={row.text}
        onChange={(text) => onUpdate(row.id, { text })}
      />
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Level</label>
        <SelectBox
          items={HEADING_LEVELS}
          displayExpr="label"
          valueExpr="value"
          value={row.level}
          onValueChanged={(e) => onUpdate(row.id, { level: e.value })}
        />
      </div>
    </>
  );
}

function SectionProperties({
  row,
  node,
  onUpdate,
}: {
  row: SectionRow;
  node: RowNode;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const hasAdvancedFilter = !!(row.filter.codes?.length || row.filter.excludeCodes?.length || row.filter.parentCode);

  return (
    <>
      <TextProperty
        label="Section Name"
        value={row.name}
        onChange={(name) => onUpdate(row.id, { name })}
      />
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Account Types</label>
        <TagBox
          items={ACCOUNT_TYPES}
          displayExpr="label"
          valueExpr="value"
          value={row.filter.types ?? []}
          onValueChanged={(e) =>
            onUpdate(row.id, { filter: { ...row.filter, types: e.value } })
          }
          showSelectionControls
          placeholder="Select account types..."
          disabled={hasAdvancedFilter}
        />
        {hasAdvancedFilter && (
          <span className="text-xs text-amber-600">
            Disabled — controlled by advanced filter
          </span>
        )}
      </div>
      <div>
        <Button
          text="Advanced Filter..."
          icon="filter"
          stylingMode="outlined"
          onClick={() => setFilterDialogOpen(true)}
        />
        {hasAdvancedFilter && (
          <Button
            text="Clear Advanced"
            icon="close"
            stylingMode="text"
            onClick={() => {
              const { codes: _c, excludeCodes: _e, parentCode: _p, ...rest } = row.filter;
              onUpdate(row.id, { filter: rest });
            }}
          />
        )}
        <AccountFilterDialog
          visible={filterDialogOpen}
          currentFilter={row.filter}
          onApply={(filter) => {
            const newFilter = { ...filter };
            if (newFilter.codes?.length || newFilter.excludeCodes?.length || newFilter.parentCode) {
              delete newFilter.types;
            }
            onUpdate(row.id, { filter: newFilter });
            setFilterDialogOpen(false);
          }}
          onCancel={() => setFilterDialogOpen(false)}
        />
      </div>
      <CheckBox
        text="Show Subtotal"
        value={row.showSubtotal}
        onValueChanged={(e) => onUpdate(row.id, { showSubtotal: e.value })}
      />
      {row.showSubtotal && (
        <TextProperty
          label="Subtotal Label"
          value={row.subtotalLabel ?? ''}
          onChange={(subtotalLabel) => onUpdate(row.id, { subtotalLabel })}
          placeholder="Subtotal"
        />
      )}
      <ColumnHeadersSelector node={node} />
    </>
  );
}

function AccountProperties({
  row,
  node,
  onUpdate,
}: {
  row: AccountRow;
  node: RowNode;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  return (
    <>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Account</label>
        <SelectBox
          items={CHILD_ACCOUNTS}
          displayExpr="display"
          valueExpr="code"
          value={row.accountCode || null}
          onValueChanged={(e) => {
            if (e.value != null) {
              onUpdate(row.id, { accountCode: e.value });
            }
          }}
          searchEnabled
          placeholder="Select account..."
        />
      </div>
      <TextProperty
        label="Label Override (optional)"
        value={row.label ?? ''}
        onChange={(label) => onUpdate(row.id, { label: label || undefined })}
        placeholder="Use account name"
      />
      <ColumnHeadersSelector node={node} />
    </>
  );
}

function SumOfEditor({
  sumOf,
  onChange,
  currentRowId,
}: {
  sumOf: string[];
  onChange: (sumOf: string[]) => void;
  currentRowId: string;
}) {
  const { nodes } = useDesignerStore();
  const flat = flattenTree(nodes);

  const displayValue = sumOf.map((id) => `[${id}]`).join(' + ');
  const [localValue, setLocalValue] = useState(displayValue);

  const availableRows = flat
    .filter((n) => n.rowId !== currentRowId)
    .map((n) => ({
      rowId: n.rowId,
      label: n.data.type === 'header' ? n.data.text
        : n.data.type === 'section' ? n.data.name
        : n.data.type === 'subtotal' ? n.data.label
        : n.data.type === 'calculated' ? n.data.label
        : n.data.type === 'grand_total' ? n.data.label
        : n.data.type === 'account' ? n.data.accountCode
        : n.rowId,
    }));

  const allRowIds = useMemo(() => new Set(flat.map((n) => n.rowId)), [flat]);

  const parseSumOfInput = useCallback((input: string): string[] => {
    const pattern = /\[([a-zA-Z_][a-zA-Z0-9_]*|\d+)\]/g;
    const ids: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(input)) !== null) {
      ids.push(match[1]!);
    }
    return ids;
  }, []);

  const unresolvedRefs = useMemo(() => {
    const parsed = parseSumOfInput(localValue);
    return parsed.filter((id) => !allRowIds.has(id));
  }, [localValue, allRowIds, parseSumOfInput]);

  const handleBlur = useCallback(() => {
    const ids = parseSumOfInput(localValue);
    onChange(ids);
  }, [localValue, onChange, parseSumOfInput]);

  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-600">Sum Of (row references)</label>
      <TextBox
        value={localValue}
        onValueChanged={(e) => setLocalValue(e.value ?? '')}
        valueChangeEvent="input"
        onFocusOut={handleBlur}
        placeholder="e.g. [10] + [20] + [REV_TOTAL]"
      />
      {unresolvedRefs.length > 0 && (
        <div className="text-xs text-amber-600">
          Unknown row ID{unresolvedRefs.length > 1 ? 's' : ''}: {unresolvedRefs.map((r) => `[${r}]`).join(', ')}
        </div>
      )}
      {availableRows.length > 0 && (
        <div className="text-xs text-gray-400">
          Available: {availableRows.map((r) => `[${r.rowId}] ${r.label}`).join(', ')}
        </div>
      )}
    </div>
  );
}

function SubtotalProperties({
  row,
  node,
  onUpdate,
}: {
  row: SubtotalRow;
  node: RowNode;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  return (
    <>
      <TextProperty
        label="Label"
        value={row.label}
        onChange={(label) => onUpdate(row.id, { label })}
      />
      <SumOfEditor
        sumOf={row.sumOf}
        onChange={(sumOf) => onUpdate(row.id, { sumOf })}
        currentRowId={node.rowId}
      />
      <StyleSelector
        value={row.style}
        onChange={(style) => onUpdate(row.id, { style })}
      />
      <ColumnHeadersSelector node={node} />
    </>
  );
}

function CalculatedProperties({
  row,
  node,
  onUpdate,
}: {
  row: CalculatedRow;
  node: RowNode;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  return (
    <>
      <TextProperty
        label="Label"
        value={row.label}
        onChange={(label) => onUpdate(row.id, { label })}
      />
      <FormulaEditor
        formula={row.formula}
        onChange={(formula) => onUpdate(row.id, { formula })}
        currentRowId={node.rowId}
      />
      <StyleSelector
        value={row.style}
        onChange={(style) => onUpdate(row.id, { style })}
      />
      <ColumnHeadersSelector node={node} />
    </>
  );
}

function GrandTotalProperties({
  row,
  node,
  onUpdate,
}: {
  row: GrandTotalRow;
  node: RowNode;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  return (
    <>
      <TextProperty
        label="Label"
        value={row.label}
        onChange={(label) => onUpdate(row.id, { label })}
      />
      <SumOfEditor
        sumOf={row.sumOf}
        onChange={(sumOf) => onUpdate(row.id, { sumOf })}
        currentRowId={node.rowId}
      />
      <ColumnHeadersSelector node={node} />
    </>
  );
}

function TitleBlockProperties({
  row,
  onUpdate,
}: {
  row: TitleBlockRow;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  return (
    <>
      <TextProperty
        label="Text"
        value={row.text}
        onChange={(text) => onUpdate(row.id, { text })}
        placeholder="Report Title"
      />
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Alignment</label>
        <SelectBox
          items={TITLE_BLOCK_ALIGNMENTS}
          displayExpr="label"
          valueExpr="value"
          value={row.alignment}
          onValueChanged={(e) => onUpdate(row.id, { alignment: e.value })}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Font Size</label>
        <NumberBox
          value={row.fontSize ?? 11}
          min={6}
          max={48}
          step={1}
          showSpinButtons
          onValueChanged={(e) => onUpdate(row.id, { fontSize: e.value ?? undefined })}
        />
      </div>
      <div className="flex gap-4">
        <CheckBox
          text="Bold"
          value={row.bold ?? false}
          onValueChanged={(e) => onUpdate(row.id, { bold: e.value ?? undefined })}
        />
        <CheckBox
          text="Italic"
          value={row.italic ?? false}
          onValueChanged={(e) => onUpdate(row.id, { italic: e.value ?? undefined })}
        />
      </div>
      <div className="text-xs text-gray-400 bg-gray-50 rounded p-2">
        <span className="font-medium text-gray-500">Available tokens:</span>
        <br />
        <code className="text-gray-600">{'{Company}'}</code> — resolves to company parameter
        <br />
        <code className="text-gray-600">{'{Period}'}</code> — resolves to period parameter
      </div>
    </>
  );
}

function ColumnHeadersProperties({
  row,
  onUpdate,
}: {
  row: ColumnHeadersRow;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  const { setColumnModalOpen } = useDesignerStore();

  return (
    <>
      <TextProperty
        label="Label (optional)"
        value={row.label ?? ''}
        onChange={(label) => onUpdate(row.id, { label: label || undefined })}
        placeholder="e.g. Revenue Columns"
      />
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Columns</label>
        <div className="text-sm text-gray-700 bg-gray-50 rounded p-2 space-y-1">
          <p className="font-medium">{row.columns.length} column{row.columns.length !== 1 ? 's' : ''}</p>
          <ul className="text-xs text-gray-500 list-disc pl-4">
            {row.columns.map((col) => (
              <li key={col.id}>{col.header} ({col.type})</li>
            ))}
          </ul>
        </div>
      </div>
      <Button
        text="Edit Column Definitions..."
        icon="columnfield"
        stylingMode="outlined"
        width="100%"
        onClick={() => setColumnModalOpen(true)}
      />
    </>
  );
}

function LineProperties({
  row,
  onUpdate,
}: {
  row: LineRow;
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Border Style</label>
      <SelectBox
        items={LINE_BORDER_STYLES}
        displayExpr="label"
        valueExpr="value"
        value={row.borderStyle}
        onValueChanged={(e) => onUpdate(row.id, { borderStyle: e.value })}
      />
    </div>
  );
}

// ============================================================
// Main PropertyPanel
// ============================================================

export function PropertyPanel() {
  const { nodes, selectedRowId, updateNode } = useDesignerStore();
  const flat = flattenTree(nodes);
  const selectedNode = flat.find((n) => n.id === selectedRowId) ?? null;

  if (!selectedNode) {
    return (
      <div className="p-4 text-sm text-gray-400 italic">
        Select a row to edit its properties
      </div>
    );
  }

  const selectedRow = selectedNode.data;

  return (
    <div key={selectedNode.id} className="p-4 space-y-4">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
        {selectedRow.type.replace('_', ' ')} Properties
      </h3>
      <RowIdField node={selectedNode} />
      {renderProperties(selectedRow, selectedNode, updateNode)}
    </div>
  );
}

function renderProperties(
  row: RowDefinition,
  node: RowNode,
  onUpdate: (id: string, updates: Partial<RowDefinition>) => void,
) {
  switch (row.type) {
    case 'header':
      return <HeaderProperties row={row} onUpdate={onUpdate} />;
    case 'section':
      return <SectionProperties row={row} node={node} onUpdate={onUpdate} />;
    case 'account':
      return <AccountProperties row={row} node={node} onUpdate={onUpdate} />;
    case 'subtotal':
      return <SubtotalProperties row={row} node={node} onUpdate={onUpdate} />;
    case 'calculated':
      return <CalculatedProperties row={row} node={node} onUpdate={onUpdate} />;
    case 'spacer':
      return <p className="text-sm text-gray-400 italic">No configurable properties</p>;
    case 'line':
      return <LineProperties row={row} onUpdate={onUpdate} />;
    case 'grand_total':
      return <GrandTotalProperties row={row} node={node} onUpdate={onUpdate} />;
    case 'title_block':
      return <TitleBlockProperties row={row} onUpdate={onUpdate} />;
    case 'column_headers':
      return <ColumnHeadersProperties row={row} onUpdate={onUpdate} />;
  }
}
